import get from 'lodash/get';
import { runMingo, unFlatten } from '@buddy-technology/ion-helpers';
import hasCalculate from '../hasCalculate';
import parseDynoTag from './parseDynoTag';
import { applyFilters } from './filters';

const getAllowedValue = (value) => {
	const type = typeof value;
	const disallowedTypes = ['symbol', 'undefined', 'object', 'function'];
	if (disallowedTypes.includes(type)) {
		return '';
	}
	if (type === 'number' && !Number.isFinite(value)) {
		return '';
	}
	return value;
};

const getValue = (path, state, variables, offeringOptions) => {
	const formattedState = unFlatten(state);
	const raw = get({ variables, offeringOptions, ...formattedState }, path);

	if (hasCalculate(raw)) {
		// keeping the noise down, we're not going to log errors here since a lot of dynomark is contingent on user state
		const { value } = runMingo(raw._calculate, { ...formattedState, variables, offeringOptions });
		if ([undefined, null].includes(value)) {
			return '';
			// if there's no value, return empty string (so markdown doesn't render 'undefined' or 'null')
			// NaN will still be returned here, so devs can catch/correct those issues sooner.
		}
		return value;
	}
	return raw;
};

/**
 * Processes dynamic markdown (DynoMark) and returns the interpolated string.
 * Supports pipe filters: `{{ path | upperCase }}`, `{{ path | date }}`, etc.
 * @param  {String} text - the raw DynoMark text to be processed
 * @param  {Object} [state = {}] - form/application state object
 * @param  {Object} [variables = {}] - any variables needed for processing
 * @param  {Object} [offeringOptions = {}]
 * @returns {String} - the processed markdown string
 */
function getDynoMark(
	text,
	state = {},
	variables = {},
	offeringOptions = {},
	_depth = 0, // private param for recursion depth
) {
	const processed = text.replace(/\{\{(.*?)\}\}/g, (match) => {
		const { path, filters } = parseDynoTag(match);
		if (!path) {
			return '';
		}
		const rawValue = getValue(path, state, variables, offeringOptions);
		// If filters are present and the resolved value itself contains dyno tags,
		// resolve the nested tags first so string filters don't corrupt the inner tag syntax.
		let resolvedValue = rawValue;
		if (filters.length > 0 && typeof rawValue === 'string' && /\{\{.*?\}\}/.test(rawValue) && _depth < 3) {
			resolvedValue = getDynoMark(rawValue, state, variables, offeringOptions, _depth + 1);
		}
		const filtered = applyFilters(resolvedValue, filters);
		return getAllowedValue(filtered);
	});

	if (/\{\{.*?\}\}/.test(processed) && _depth < 3) { // limit recursion to 3 levels
		return getDynoMark(processed, state, variables, offeringOptions, _depth + 1);
	}

	return processed;
}

export default getDynoMark;
